// frontend/src/components/messaging/MessageThread.jsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  Send, 
  Paperclip, 
  X, 
  Clock, 
  Check, 
  CheckCheck,
  User,
  Store,
  Package,
  ChevronLeft,
  Search,
  Filter
} from 'lucide-react';
import { useMessaging } from '../../context/MessagingContext';
import { useAuth } from '../../context/AuthContext';
import { formatDistanceToNow } from '../../utils/dateUtils';

const MessageThread = ({ onClose, recipientId, recipientName, recipientType, orderRef }) => {
  const { 
    threads, 
    activeThread, 
    sendMessage, 
    selectThread,
    getOrCreateThread,
    quickReplies 
  } = useMessaging();
  
  const { getActiveUser, vendorAuth, memberAuth } = useAuth();
  let user = getActiveUser();

  // Fallback: if getActiveUser returns null on vendor routes, try vendorAuth directly
  if (!user && window.location.pathname.startsWith('/vendor')) {
    user = vendorAuth;
  }

  // If still no user, try to get from localStorage as last resort
  if (!user && window.location.pathname.startsWith('/vendor')) {
    const vendorData = localStorage.getItem('currentVendor');
    if (vendorData) {
      try {
        user = JSON.parse(vendorData);
        user.userType = 'vendor';
      } catch (e) {
        console.error('Error parsing vendor data:', e);
      }
    }
  }
  
  const [message, setMessage] = useState('');
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showThreadList, setShowThreadList] = useState(!recipientId);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const hasInitialized = useRef(false);

  // Initialize thread if recipientId provided - FIX THE INFINITE LOOP
  useEffect(() => {
    // Only run once per recipientId
    if (!hasInitialized.current && recipientId && user) {
      hasInitialized.current = true;
      const thread = getOrCreateThread(recipientId, recipientName, recipientType, orderRef);
      if (thread) {
        selectThread(thread.id);
        setShowThreadList(false);
      }
    }
  }, [recipientId, user, getOrCreateThread, selectThread, recipientName, recipientType, orderRef]);

  // Reset initialization flag when recipientId changes
  useEffect(() => {
    hasInitialized.current = false;
  }, [recipientId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeThread?.messages]);

  // Keep focus on input when activeThread changes
  useEffect(() => {
    if (activeThread && inputRef.current) {
      inputRef.current.focus();
    }
  }, [activeThread?.id]);

  // Filter threads based on search
  const filteredThreads = threads.filter(thread => {
    const otherParticipant = thread.participants.find(p => p !== user?.id);
    const participantName = thread.participantNames[otherParticipant] || '';
    return participantName.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleSend = () => {
    if (!message.trim() || !activeThread) return;
    
    sendMessage(activeThread.id, message.trim());
    setMessage('');
    setShowQuickReplies(false);
    inputRef.current?.focus();
  };

  const handleQuickReply = (reply) => {
    setMessage(reply);
    setShowQuickReplies(false);
    inputRef.current?.focus();
  };

  const getParticipantIcon = (type) => {
    switch (type) {
      case 'vendor':
        return <Store size={16} />;
      case 'admin':
        return <User size={16} />;
      default:
        return <User size={16} />;
    }
  };

  const getMessageStatus = (msg) => {
    if (msg.senderId === user?.id) {
      if (msg.read) {
        return <CheckCheck size={14} className="text-blue-500" />;
      }
      return <Check size={14} className="text-gray-400" />;
    }
    return null;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } else if (diffInHours < 168) { // Less than a week
      return date.toLocaleDateString('en-US', { 
        weekday: 'short',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  // Add null check for user
  if (!user) {
    return (
      <div className="h-full bg-white rounded-lg shadow-lg overflow-hidden flex items-center justify-center">
        <p className="text-gray-500">Loading messages...</p>
      </div>
    );
  }

  // Thread List View
  if (showThreadList && !activeThread) {
    return (
      <div className="h-full bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold">Messages</h2>
            <div className="mt-3 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Thread List */}
          <div className="flex-1 overflow-y-auto">
            {filteredThreads.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <p className="mb-2">No messages yet</p>
                <p className="text-sm">Start a conversation with a {user?.userType === 'vendor' ? 'group admin' : 'vendor'}</p>
              </div>
            ) : (
              filteredThreads.map(thread => {
                const otherParticipant = thread.participants.find(p => p !== user?.id);
                const unreadMessages = thread.messages.filter(m => !m.read && m.senderId !== user?.id).length;
                
                return (
                  <button
                    key={thread.id}
                    onClick={() => {
                      selectThread(thread.id);
                      setShowThreadList(false);
                    }}
                    className="w-full p-4 hover:bg-gray-50 flex items-start space-x-3 border-b transition-colors"
                  >
                    <div className="flex-shrink-0 w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                      {getParticipantIcon(thread.participantTypes[otherParticipant])}
                    </div>
                    <div className="flex-1 text-left">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-medium text-gray-900">
                          {thread.participantNames[otherParticipant]}
                        </h3>
                        {thread.lastMessage && (
                          <span className="text-xs text-gray-500">
                            {formatTimestamp(thread.lastMessage.timestamp)}
                          </span>
                        )}
                      </div>
                      {thread.orderRef && (
                        <div className="flex items-center text-xs text-gray-500 mb-1">
                          <Package size={12} className="mr-1" />
                          Order #{thread.orderRef.split('_')[1]}
                        </div>
                      )}
                      <p className="text-sm text-gray-600 truncate">
                        {thread.lastMessage ? thread.lastMessage.content : 'No messages yet'}
                      </p>
                    </div>
                    {unreadMessages > 0 && (
                      <div className="flex-shrink-0">
                        <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-blue-600 rounded-full">
                          {unreadMessages}
                        </span>
                      </div>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>
    );
  }

  // Conversation View
  if (!activeThread) return null;
  
  const otherParticipant = activeThread.participants.find(p => p !== user?.id);
  
  return (
    <div className="h-full bg-white rounded-lg shadow-lg overflow-hidden">
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="p-4 border-b bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowThreadList(true)}
                className="p-2 hover:bg-gray-100 rounded-lg md:hidden"
              >
                <ChevronLeft size={20} />
              </button>
              <div className="flex-shrink-0 w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                {getParticipantIcon(activeThread.participantTypes[otherParticipant])}
              </div>
              <div>
                <h3 className="font-semibold">{activeThread.participantNames[otherParticipant]}</h3>
                {activeThread.orderRef && (
                  <p className="text-xs text-gray-500">
                    Order #{activeThread.orderRef.split('_')[1]}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {activeThread.messages.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <p>No messages yet. Start the conversation!</p>
            </div>
          ) : (
            activeThread.messages.map((msg, index) => {
              const isOwn = msg.senderId === user?.id;
              const showTimestamp = index === 0 || 
                (new Date(msg.timestamp) - new Date(activeThread.messages[index - 1].timestamp)) > 300000; // 5 minutes
              
              return (
                <div key={msg.id}>
                  {showTimestamp && (
                    <div className="text-center text-xs text-gray-500 my-2">
                      {formatTimestamp(msg.timestamp)}
                    </div>
                  )}
                  <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] ${isOwn ? 'order-2' : 'order-1'}`}>
                      <div className={`rounded-lg px-4 py-2 ${
                        isOwn 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-gray-100 text-gray-900'
                      }`}>
                        <p className="text-sm">{msg.content}</p>
                      </div>
                      <div className={`flex items-center mt-1 text-xs text-gray-500 ${
                        isOwn ? 'justify-end' : 'justify-start'
                      }`}>
                        <span>{formatTimestamp(msg.timestamp)}</span>
                        {isOwn && (
                          <span className="ml-2">
                            {getMessageStatus(msg)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Replies */}
        {showQuickReplies && (
          <div className="p-3 border-t bg-gray-50">
            <p className="text-xs text-gray-600 mb-2">Quick replies:</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(quickReplies[user?.userType] || {}).map(([key, text]) => (
                <button
                  key={key}
                  onClick={() => handleQuickReply(text)}
                  className="px-3 py-1 text-sm bg-white border rounded-full hover:bg-gray-100"
                >
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="p-4 border-t bg-white">
          <div className="flex items-end space-x-2">
            <button
              onClick={() => setShowQuickReplies(!showQuickReplies)}
              className="p-2 hover:bg-gray-100 rounded-lg"
              title="Quick replies"
            >
              <Clock size={20} className="text-gray-600" />
            </button>
            <div className="flex-1">
              <textarea
                ref={inputRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder="Type a message..."
                className="w-full px-4 py-2 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="1"
                style={{ minHeight: '40px', maxHeight: '120px' }}
                autoFocus
              />
            </div>
            <button
              onClick={handleSend}
              disabled={!message.trim()}
              className={`p-2 rounded-lg transition-colors ${
                message.trim() 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-gray-100 text-gray-400'
              }`}
            >
              <Send size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageThread;